import React, { useId } from "react";
import { btn3d } from "../styles/buttonStyles";

function ConsentItem({
  idPrefix,
  dm,
  checked,
  onCheckedChange,
  title,
  expanded,
  onExpandedChange,
  children,
}) {
  const checkboxId = `${idPrefix}-checkbox`;
  const detailsId = `${idPrefix}-details`;

  return (
    <section style={{
      borderRadius: 14,
      border: `1px solid ${dm ? "rgba(167,139,250,0.22)" : "rgba(99,102,241,0.2)"}`,
      overflow: "hidden",
    }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "12px 13px",
        background: dm ? "rgba(167,139,250,0.07)" : "rgba(238,242,255,0.9)",
      }}>
        <input
          id={checkboxId}
          type="checkbox"
          checked={checked}
          onChange={event => onCheckedChange(event.target.checked)}
          style={{
            width: 21,
            height: 21,
            flexShrink: 0,
            accentColor: "var(--accent)",
            cursor: "pointer",
          }}
        />
        <label htmlFor={checkboxId} className="text-sm" style={{
          flex: 1,
          minWidth: 0,
          color: "var(--text)",
          fontWeight: 750,
          lineHeight: 1.45,
          cursor: "pointer",
          wordBreak: "keep-all",
        }}>
          {title} <span className="text-xs" style={{ color: "#E05040", fontWeight: 800 }}>(필수)</span>
        </label>
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={detailsId}
          onClick={() => onExpandedChange(!expanded)}
          className="btn3d text-xs"
          style={{
            ...btn3d("outline", dm),
            padding: "7px 9px",
            minHeight: 34,
            borderRadius: 9,
            flexShrink: 0,
            fontWeight: 800,
            whiteSpace: "nowrap",
          }}
        >
          {expanded ? "접기" : "상세"}
        </button>
      </div>
      {expanded && (
        <div
          id={detailsId}
          className="text-xs"
          style={{
            padding: "14px 15px",
            background: dm ? "rgba(0,0,0,0.2)" : "rgba(248,250,252,0.95)",
            borderTop: `1px dashed ${dm ? "rgba(167,139,250,0.18)" : "rgba(99,102,241,0.18)"}`,
            color: "var(--muted)",
            lineHeight: 1.72,
          }}
        >
          {children}
        </div>
      )}
    </section>
  );
}

const DetailRow = ({ label, children }) => (
  <p style={{ marginBottom: 8 }}>
    <strong style={{ color: "var(--accent)" }}>{label}</strong><br />
    {children}
  </p>
);

export default function PrivacyConsentFields({
  dm,
  privacyAgreed,
  setPrivacyAgreed,
  privacyExpanded,
  setPrivacyExpanded,
  overseasTransferAgreed,
  setOverseasTransferAgreed,
  overseasTransferExpanded,
  setOverseasTransferExpanded,
}) {
  const generatedId = useId().replace(/:/g, "");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 18 }}>
      <ConsentItem
        idPrefix={`privacy-${generatedId}`}
        dm={dm}
        checked={privacyAgreed}
        onCheckedChange={setPrivacyAgreed}
        title="개인정보 수집 및 이용 동의"
        expanded={privacyExpanded}
        onExpandedChange={setPrivacyExpanded}
      >
        <DetailRow label="수집 항목">
          이름, 나이대, 성별, 거주지, 이메일 및 입력한 경우 혈액형·MBTI·별자리
        </DetailRow>
        <DetailRow label="수집·이용 목적">
          회원 프로필 관리, AI 맞춤 여행 일정 및 성향 분석, 여행 일정 저장, 사용자가 선택한 커뮤니티 공유
        </DetailRow>
        <DetailRow label="저장 위치와 보유 기간">
          회원가입 또는 프로필 저장 시 Firebase(Google Cloud)에 저장되며, 회원 탈퇴가 완료될 때까지 보관됩니다.
          내 정보의 회원 탈퇴 메뉴에서 삭제를 요청하고 직접 처리할 수 있습니다. 탈퇴 시 프로필과 작성자 식별 정보는
          삭제되며, 저장 일정·커뮤니티 공유 일정·후기 콘텐츠는 개인 식별 정보 제거 후 익명 기록으로 보존됩니다.
        </DetailRow>
        <DetailRow label="동의 거부 권리와 불이익">
          동의를 거부할 수 있으나, 프로필 저장과 개인정보를 사용하는 맞춤 일정·커뮤니티 기능 이용이 제한됩니다.
        </DetailRow>
        <a
          href="https://pjh0924.github.io/tripcanvas-privacy/"
          target="_blank"
          rel="noreferrer"
          style={{ color: "var(--teal)", fontWeight: 800, textUnderlineOffset: 2 }}
        >
          개인정보처리방침 전문 보기 →
        </a>
      </ConsentItem>

      <ConsentItem
        idPrefix={`overseas-${generatedId}`}
        dm={dm}
        checked={overseasTransferAgreed}
        onCheckedChange={setOverseasTransferAgreed}
        title="개인정보 국외 이전 동의"
        expanded={overseasTransferExpanded}
        onExpandedChange={setOverseasTransferExpanded}
      >
        <DetailRow label="이전받는 자">
          OpenRouter(AI 모델 제공 중개) 및 이를 통해 연결되는 AI 모델 제공자
        </DetailRow>
        <DetailRow label="이전 국가">
          미국 등 해외
        </DetailRow>
        <DetailRow label="이전 항목">
          이름, 나이대, 성별, 여행 정보(출발지·목적지·날짜·출발 시간·스타일·교통수단·동반자 유형),
          생성된 기존 일정, 일정 수정 채팅에 사용자가 입력한 문구, 그리고 입력한 경우 MBTI·혈액형·별자리
        </DetailRow>
        <DetailRow label="이전 목적">
          AI 맞춤 여행 일정 생성, 여행 체크리스트 생성, 일정 수정 채팅 처리
        </DetailRow>
        <DetailRow label="이전 일시·방법">
          일정 생성·체크리스트 생성·일정 수정 채팅 요청 시 네트워크 전송
        </DetailRow>
        <DetailRow label="보유·이용 기간">
          일정·체크리스트 생성 또는 일정 수정 채팅 처리 후의 보유·이용은 각 AI 제공자 정책에 따릅니다.
        </DetailRow>
        <DetailRow label="동의 거부 권리와 불이익">
          동의를 거부할 수 있으나, 거부 시 AI 일정 생성·체크리스트 생성·일정 수정 채팅 기능을 이용할 수 없습니다.
        </DetailRow>
      </ConsentItem>
    </div>
  );
}
